import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  ImageKey,
  ImageWithType,
  PunchListItemReplacement,
} from '../../types.ts/general';
import { runInAction } from 'mobx';

import PhotoModal, { Img } from '../common/PhotosModal';
import { FileData, PhotoTab, PunchListItem } from '../../types.ts/story';
import RotatingSquare from '../../svgs/RotatingSquare';
import styled, { css } from 'styled-components';
import { getRandomFileName } from '../../utility/general';
import {
  getAttributionForPhoto,
  searchPhotos,
  trackDownload,
} from '../../utility/unsplash';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { useStore } from '@src/stores-v2/StoreContext';
import PhotoIcon from '@src/svgs/PhotoIcon';
import {
  useFileUpload,
  getUploadTypeFromPhotoTab,
} from '../../hooks/useFileUpload';

type Props = {
  modalItem: PunchListItem;
  setReplacement: Dispatch<SetStateAction<PunchListItemReplacement | null>>;
  onClose: () => void;
  openPrevModal: () => void;
};

const GeneratedPunchListModalItems = (props: Props) => {
  const videoCreator = useVideoCreatorStore();
  const { datoClientStore } = useStore();

  const { modalItem, setReplacement, onClose } = props;
  const [stock, setStock] = useState<Img[]>();
  const [ai, setAi] = useState<(Img & { id: string })[]>();
  const [selectedImage, setSelectedImage] = useState<
    ImageWithType[ImageKey] | null
  >(null);
  const [isLoading, setIsLoading] = useState(false);
  const aiRef = useRef(false);
  const stockRef = useRef(false);
  const [keywords, setKeywords] = useState<string[]>(
    modalItem.stockKeywords
      ?.trim()
      ?.split(',')
      .map((k) => k.trim()),
  );

  const [dallePrompt, setDallePrompt] = useState<string>(modalItem.dallePrompt);

  const generateAIImages = useCallback(() => {
    (async () => {
      const aiResponse =
        modalItem.dalleImages?.map((data) => ({
          url: data.uploadUrl,
          id: data.uploadId,
          alt: '',
          width: '',
          height: '',
        })) || [];

      setAi(aiResponse);
    })();
  }, [ai]);

  const generateStockImages = useCallback(() => {
    (async () => {
      const { photos } = await searchPhotos({
        query: modalItem.stockKeywords,
        orientation: 'landscape',
      });
      const stockData = (photos?.map((res) => ({
        url: res.urls.regular,
        alt: res.alt_description,
        width: res.width,
        height: res.height,
        attribution: getAttributionForPhoto(res),
      })) || []) as unknown as Img[];
      setStock(stockData);
    })();
  }, [stock]);

  useEffect(() => {
    if (aiRef.current) return;
    try {
      generateAIImages();
    } catch (error) {
      console.log(error);
    }
    return () => {
      aiRef.current = true;
    };
  }, []);

  useEffect(() => {
    if (stockRef.current) return;
    try {
      generateStockImages();
    } catch (error) {
      console.log(error);
    }
    return () => {
      stockRef.current = true;
    };
  }, []);

  const replaceAction = async () => {
    if (setReplacement && selectedImage) {
      try {
        if (selectedImage.type === 'ai') {
          setIsLoading(true);

          let uploadId = selectedImage.id;
          let url = selectedImage.url;

          if (!uploadId && url.startsWith('data:image')) {
            const fileName = getRandomFileName(dallePrompt);
            const newPhotoData: FileData & { fileName: string } = {
              type: 'ai',
              url: selectedImage.url,
              fileName,
              alt: dallePrompt.slice(0, 150),
              title: dallePrompt.slice(0, 150),
            };
            const newUpload =
              await datoClientStore.assetRepository?.uploadFile(newPhotoData);
            uploadId = newUpload!.id;
            url = newUpload!.url;
          }

          runInAction(() => {
            videoCreator.photoDataForDato = {
              ...videoCreator.photoDataForDato,
              [modalItem.id!]: {
                type: selectedImage.type,
                url, //todo deleted some stuff when merging
                uploadId,
              },
            };
          });
          setReplacement({
            ...selectedImage,
            aspectRatio: +selectedImage.width / +selectedImage.height,
            url: url || '',
            id: modalItem.id!,
          });
        } else {
          runInAction(() => {
            videoCreator.photoDataForDato = {
              ...videoCreator.photoDataForDato,
              [modalItem.id!]: {
                type: selectedImage.type,
                url: selectedImage.url,
                fileName: '', //todo
              },
            };
          });
          setReplacement({
            ...selectedImage,
            aspectRatio: +selectedImage.width / +selectedImage.height,
            id: modalItem.id!,
          });
          if (selectedImage.type === 'stock') {
            trackDownload(selectedImage.attribution?.download_location);
          }
        }
      } catch (error) {
        console.log('error:', error);
      } finally {
        setIsLoading(false);
        props.onClose();
      }
    }
  };

  const TopComponent = () => (
    <TopWrapper>
      <SubTitle>Swap or search for a new image</SubTitle>
      <Content>
        <Image>
          <img src={modalItem.artifactSrc} />
        </Image>
        <div className="content">
          <div className="line">{modalItem.line}</div>
          <div className="description">{modalItem.description}</div>
        </div>
      </Content>
    </TopWrapper>
  );

  return (
    <ModalWrapper>
      <PhotoModal
        TopComponent={(_) => <TopComponent />}
        externalPhotos={{ ai, stock }}
        modalItemType={modalItem.type}
        otherFields={['org_photos']}
        onCloseSelf={props.onClose}
        openPrevModal={props.openPrevModal}
        setSelectedImage={setSelectedImage}
        selectedImageUrl={selectedImage?.url || null}
        isLoading={isLoading}
        keywords={keywords}
        setKeywords={setKeywords}
        dallePrompt={dallePrompt}
        setDallePrompt={setDallePrompt}
        BottomComponent={({ tab }) => (
          <BottomComponent
            tab={tab}
            replaceAction={replaceAction}
            selectedImage={selectedImage}
            setSelectedImage={setSelectedImage}
          />
        )}
        searchBarRadius="10px"
      />
    </ModalWrapper>
  );
};

const BottomComponent = (props: {
  tab: PhotoTab;
  replaceAction: () => void;
  selectedImage: ImageWithType[ImageKey] | null;
  setSelectedImage: Dispatch<SetStateAction<ImageWithType[ImageKey] | null>>;
}) => {
  const { tab, replaceAction, selectedImage, setSelectedImage } = props;
  const { acceptFormats, handleUploadFile, isUploading } = useFileUpload({
    acceptType: 'image',
    uploadType: getUploadTypeFromPhotoTab(tab),
    onUploadComplete: (artifact) => {
      const imageWithType: ImageWithType[ImageKey] = {
        id: artifact[0].id,
        url: artifact[0].url,
        type: 'artifact',
        width: artifact[0].width || '',
        height: artifact[0].height || '',
        alt: artifact[0].title || '',
      };
      setSelectedImage(imageWithType);
    },
  });
  return (
    <ButtonContainer>
      <ActionButton isActive={!!selectedImage} onClick={replaceAction}>
        <RotatingSquare strokeColor={!selectedImage ? '#484848' : undefined} />
        Replace
      </ActionButton>
      <ActionButton isOutline disabled={isUploading}>
        <PhotoIcon strokeColor="#17C964" />
        {isUploading ? 'Uploading...' : 'Upload Image'}
        <FileInput
          key={isUploading ? 'upload-disabled' : 'upload-enabled'}
          type="file"
          accept={acceptFormats?.join(',') || 'image/*'}
          onChange={handleUploadFile}
          disabled={isUploading}
        />
      </ActionButton>
    </ButtonContainer>
  );
};

export default GeneratedPunchListModalItems;

const ModalWrapper = styled.div`
  background-color: #030419;
  color: #f3e9d7;
  width: 560px;
  box-shadow: 8px 8px 16px 0px rgba(0, 0, 0, 0.4);
  border-radius: 10px;
  padding: 20px 0;
  border: 1px solid #484848;
`;

const SubTitle = styled.h5`
  color: #f3e9d7;
  text-align: center;
  font-size: 20px;
  font-weight: 700;
  margin-top: 0;
`;
const Image = styled.div`
  width: min(215px, 45%);
  height: 116px;
  img {
    width: 100%;
    height: 100%;
    border-radius: 10px;
    object-fit: cover;
    object-position: top;
  }
`;

const Content = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  .content {
    flex: 1;
    text-align: left;
    display: flex;
    flex-direction: column;
    gap: 5px;
    .line,
    .description {
      display: -webkit-box;
      -webkit-line-clamp: 4;
      -webkit-box-orient: vertical;
      overflow: hidden;
      max-height: 40px;
      overflow-y: hidden;
    }
    .line {
      color: #45d483;
      font-size: 12px;
    }
    .description {
      color: #bdbdbd;
      font-size: 10px;
      font-weight: 300;
      line-height: 12px;
    }
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 10px;
  justify-content: center;
  padding-top: 15px;
`;

const ActionButton = styled.button<{
  isActive?: boolean;
  isOutline?: boolean;
}>`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  color: #484848;
  border: 1px solid #484848;
  border-radius: 8px;
  width: 200px;
  height: 48px;
  gap: 8px;
  background: #03041a;
  font-weight: 800;
  outline: none;
  position: relative;
  ${(props) =>
    props.isActive &&
    css`
      background: #17c964;
      color: #03041a;
      border: 1px solid #17c964;
    `}
  ${(props) =>
    props.isOutline &&
    css`
      border: 1px solid #17c964;
      color: #17c964;
    `}
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const FileInput = styled.input`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
  &:disabled {
    cursor: not-allowed;
  }
`;

const TopWrapper = styled.div`
  padding: 20px;
  margin-bottom: 10px;
`;
